/** @format */
import { Injectable } from '@angular/core';
import { ValidatablePlan } from 'common-typescript';
import {
    CourseUnit,
    CourseUnitAttainment,
    CreditRange,
    CustomCourseUnitAttainment,
    CustomStudyDraft,
    Module,
} from 'common-typescript/types';
import { ModuleDescendantService } from '../service/module-descendant.service';

@Injectable()
export class CreditSumService {
    constructor(private readonly _moduleDescendantService: ModuleDescendantService) {}

    /**
     * Returns a sum of credits for timed (not attained) and attained course units (includes custom course unit attainments)
     * and timed custom study drafts that are selected in the given plan under the given module.
     * If a course unit is substituted, the substituting course unit's credits are included instead.
     */
    getCreditSum(validatablePlan: ValidatablePlan, module: Module): CreditSum {
        const courseUnits: Set<CourseUnit> = this._moduleDescendantService.getCourseUnitsForModuleRecursively(module, validatablePlan);
        // minimum is always 0, but it's possible that maximum is unlimited
        let timedCreditsMin: number = 0;
        // null stands for infinity
        let timedCreditsMax: number | null | undefined;
        let attainedCredits: number = 0;

        for (const courseUnit of courseUnits) {
            const attainment: CourseUnitAttainment | null = validatablePlan.getCourseUnitAttainment(courseUnit.id);
            if (attainment) {
                attainedCredits += attainment.credits;
            } else if (validatablePlan.isCourseUnitTimed(courseUnit.id)) {
                if (courseUnit.credits) {
                    if (Number.isFinite(courseUnit.credits.min)) {
                        timedCreditsMin += courseUnit.credits.min;
                    }

                    if (timedCreditsMax !== null) {
                        if (Number.isFinite(courseUnit.credits.max)) {
                            timedCreditsMax = (timedCreditsMax ?? 0) + courseUnit.credits.max;
                        } else {
                            timedCreditsMax = null;
                        }
                    }
                }
            }
        }

        // custom study drafts
        const customStudyDrafts: Set<CustomStudyDraft> = this._moduleDescendantService.getCustomStudyDraftsForModuleRecursively(
            module,
            validatablePlan,
        );
        for (const customStudyDraft of customStudyDrafts) {
            if (validatablePlan.isCustomStudyDraftTimed(customStudyDraft.id) && customStudyDraft.credits) {
                timedCreditsMin += customStudyDraft.credits;
                if (timedCreditsMax !== null) {
                    timedCreditsMax = (timedCreditsMax ?? 0) + customStudyDraft.credits;
                }
            }
        }

        // custom course unit attainments
        const customCourseUnitAttainmentsInPlan: readonly CustomCourseUnitAttainment[] =
            validatablePlan?.getAllCustomCourseUnitAttainmentsInPlan() ?? [];
        for (const customCourseUnitAttainment of customCourseUnitAttainmentsInPlan) {
            attainedCredits += customCourseUnitAttainment.credits;
        }

        // if minimum is 0 and there's no maximum (not even infinity), maximum will be 0 too
        if (timedCreditsMin === 0 && timedCreditsMax === undefined) {
            timedCreditsMax = 0;
        }

        return {
            timedCredits: { min: timedCreditsMin, max: timedCreditsMax },
            attainedCredits,
        };
    }
}

export interface CreditSum {
    /**
     * Summed credits of timed but not attained course units and timed custom study drafts.
     * The range always has a {@link CreditRange.min},
     * but can have null {@link CreditRange.max},
     * if any of the summed course units lacks the maximum.
     */
    readonly timedCredits: CreditRange;

    /**
     * Summed credits of attained course units (includes custom course unit attainments), whether timed or not.
     */
    readonly attainedCredits: number;
}
